import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/shared/ui';
import { bulkAssignUsersToClass, type BulkAssignUsersResponseDto } from '../../services/classService';
import { handleApiError } from '@/utils/errorHandlers';

interface AddParticipantModalProps {
	classId: string | number;
	onSuccess?: () => void;
}

export default function AddParticipantModal({ classId, onSuccess }: AddParticipantModalProps): React.ReactElement {
	const { t } = useTranslation('class');
	const [loginIdsText, setLoginIdsText] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [result, setResult] = useState<BulkAssignUsersResponseDto | null>(null);

	const parseLoginIds = (text: string): string[] => {
		// 쉼표와 줄바꿈을 모두 구분자로 처리
		return text
			.split(/[,\n]/)
			.map(id => id.trim())
			.filter(id => id.length > 0);
	};

	const handleSubmit = async () => {
		const loginIds = parseLoginIds(loginIdsText);
		
		if (loginIds.length === 0) {
			alert(t('modals.addParticipant.noLoginIds', '유효한 로그인 ID를 입력해주세요.'));
			return;
		}

		console.log('🔵 [AddParticipantModal] 참여자 추가 시작', { classId, loginIds });

		setIsSubmitting(true);
		try {
			const numericClassId = typeof classId === 'string' ? parseInt(classId, 10) : classId;
			console.log('🔵 [AddParticipantModal] API 호출 중...', { numericClassId, loginIdsCount: loginIds.length });
			
			const response = await bulkAssignUsersToClass(numericClassId, loginIds);
			
			console.log('✅ [AddParticipantModal] API 응답 성공', response);
			setResult(response);
			
			// 성공 시 콜백 실행 - 결과를 본 후에 실행되도록 제거
			// if (onSuccess) {
			// 	onSuccess();
			// }
		} catch (err) {
			console.error('❌ [AddParticipantModal] API 호출 실패', err);
			const { message } = handleApiError(err, t('modals.addParticipant.failed', '참여자 추가에 실패했습니다.'));
			alert(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReset = () => {
		setLoginIdsText('');
		setResult(null);
	};

	const totalAttempted = result ? (result.assignedCount + result.skippedCount + result.notFoundLoginIds.length + result.organizationMismatchLoginIds.length) : 0;

	return (
		<div className="space-y-6 py-2">
			{!result ? (
				<div className="space-y-6">
					<div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
						<h4 className="text-blue-800 font-semibold text-sm mb-1 flex items-center gap-2">
							<UserPlus size={16} /> {t('modals.addParticipant.title', '참여자 추가')}
						</h4>
						<p className="text-blue-700 text-sm">
							{t('modals.addParticipant.description', '로그인 ID를 쉼표(,) 또는 줄바꿈으로 구분하여 입력하세요.')}
						</p>
					</div>

					<div className="space-y-3">
						<label className="text-sm font-medium text-gray-700">
							{t('modals.addParticipant.loginIdsLabel', '로그인 ID 목록')}
						</label>
						<textarea
							value={loginIdsText}
							onChange={(e) => setLoginIdsText(e.target.value)}
							placeholder={t('modals.addParticipant.placeholder', 'user001, user002\n또는\nuser001\nuser002')}
							className="w-full h-48 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm font-mono"
						/>
						<p className="text-xs text-gray-500">
							{t('modals.addParticipant.count', '{{count}}개의 ID', { count: parseLoginIds(loginIdsText).length })}
						</p>
					</div>

					<Button
						variant="primary"
						className="w-full h-12 text-base font-semibold"
						onClick={handleSubmit}
						disabled={isSubmitting || loginIdsText.trim().length === 0}
					>
						{isSubmitting ? (
							t('modals.addParticipant.submitting', '추가 중...')
						) : (
							t('modals.addParticipant.submit', '참여자 추가')
						)}
					</Button>
				</div>
			) : (
				<div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
					{result.assignedCount > 0 && (
						<div className="bg-green-50 p-4 rounded-lg border border-green-100">
							<div className="flex items-center gap-2 mb-2">
								<Check size={18} className="text-green-600" />
								<h4 className="text-green-800 font-semibold text-sm">
									{t('modals.addParticipant.resultTitle', '참여자 추가 완료')}
								</h4>
							</div>
							<p className="text-green-700 text-sm">
								{t('modals.addParticipant.resultSummary', '총 {{total}}명 중 {{assigned}}명이 추가되었습니다.', {
									total: totalAttempted,
									assigned: result.assignedCount,
								})}
							</p>
						</div>
					)}

					<div className="space-y-4">
						{result.assignedCount > 0 && (
							<div className="p-4 bg-white border border-gray-200 rounded-lg">
								<div className="flex items-center gap-2 mb-2">
									<Check size={16} className="text-green-500" />
									<span className="text-sm font-semibold text-gray-900">
										{t('modals.addParticipant.successLabel', '성공')} ({result.assignedCount})
									</span>
								</div>
								<p className="text-sm text-gray-600">
									{t('modals.addParticipant.successDescription', '{{count}}명이 클래스에 추가되었습니다.', {
										count: result.assignedCount,
									})}
								</p>
							</div>
						)}

						{result.skippedCount > 0 && (
							<div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
								<div className="flex items-center gap-2 mb-2">
									<AlertCircle size={16} className="text-yellow-600" />
									<span className="text-sm font-semibold text-yellow-800">
										{t('modals.addParticipant.skippedLabel', '건너뜀')} ({result.skippedCount})
									</span>
								</div>
								<p className="text-sm text-yellow-700">
									{t('modals.addParticipant.skippedDescription', '이미 참여 중인 사용자입니다.')}
								</p>
							</div>
						)}

						{result.notFoundLoginIds.length > 0 && (
							<div className="p-4 bg-red-50 border border-red-200 rounded-lg">
								<div className="flex items-center gap-2 mb-2">
									<AlertCircle size={16} className="text-red-600" />
									<span className="text-sm font-semibold text-red-800">
										{t('modals.addParticipant.notFoundLabel', '존재하지 않는 사용자')} ({result.notFoundLoginIds.length})
									</span>
								</div>
								<div className="text-sm text-red-700 space-y-1">
									{result.notFoundLoginIds.map((id, idx) => (
										<div key={idx} className="font-mono text-xs bg-red-100 px-2 py-1 rounded inline-block mr-2 mb-1">
											{id}
										</div>
									))}
								</div>
							</div>
						)}

						{result.organizationMismatchLoginIds.length > 0 && (
							<div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
								<div className="flex items-center gap-2 mb-2">
									<AlertCircle size={16} className="text-orange-600" />
									<span className="text-sm font-semibold text-orange-800">
										{t('modals.addParticipant.orgMismatchLabel', '기관 불일치')} ({result.organizationMismatchLoginIds.length})
									</span>
								</div>
								<p className="text-sm text-orange-700 mb-2">
									{t('modals.addParticipant.orgMismatchDescription', '클래스 기관과 일치하지 않는 사용자입니다.')}
								</p>
								<div className="text-sm text-orange-700 space-y-1">
									{result.organizationMismatchLoginIds.map((id, idx) => (
										<div key={idx} className="font-mono text-xs bg-orange-100 px-2 py-1 rounded inline-block mr-2 mb-1">
											{id}
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="flex gap-2">
						<Button
							variant="outline"
							className="flex-1 h-12 text-base font-semibold"
							onClick={handleReset}
						>
							{t('modals.addParticipant.addMore', '추가로 입력하기')}
						</Button>
						<Button
							variant="primary"
							className="flex-1 h-12 text-base font-semibold"
							onClick={() => {
								if (onSuccess) {
									onSuccess();
								}
							}}
						>
							{t('modals.addParticipant.close', '닫기')}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
